/**
 * ISO 8601 duration helpers.
 *
 * Dependency-free replacements for the subset of `moment.duration` used by
 * this project (criticalHours intervals and license periods), replicating
 * moment's parsing and serialization semantics so values previously
 * written by moment keep working unchanged.
 */

/** Parsed ISO 8601 duration with moment-compatible component semantics. */
export interface ISO8601Duration {
    /** Whole years (floor of total months / 12). */
    years: number;
    /** Remaining months after whole years are extracted (months % 12). */
    months: number;
    /** Whole weeks (floor of total days / 7). */
    weeks: number;
    /** Total days, including days that come from weeks (P1W2D -> 9). */
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

// Matches ISO 8601 durations: P[nY][nM][nW][nD][T[nH][nM][nS]] with
// optional fractional values, e.g. 'PT7H30M', 'P1Y6M', 'P3W4D', 'P0D'.
const ISO_DURATION_REGEX =
    /^P(?!$)(\d+(?:[.,]\d+)?Y)?(\d+(?:[.,]\d+)?M)?(\d+(?:[.,]\d+)?W)?(\d+(?:[.,]\d+)?D)?(?:T(?=\d)(\d+(?:[.,]\d+)?H)?(\d+(?:[.,]\d+)?M)?(\d+(?:[.,]\d+)?S)?)?$/;

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// moment's month-to-day conversion: average month length over a
// 400-year Gregorian cycle (146097 days / 4800 months).
const daysFromMonths = (months: number): number =>
    Math.round((months * 146097) / 4800);

const toNumber = (value: string | undefined): number =>
    value ? parseFloat(value.replace(',', '.')) : 0;

/**
 * Parses an ISO 8601 duration string (e.g. 'PT7H30M', 'P1Y6M', 'P0D').
 *
 * Component getters follow moment.duration semantics: weeks and days are
 * derived from the total day count (P1W2D -> weeks: 1, days: 9) and years
 * are derived from the total month count (P1Y6M -> years: 1, months: 6).
 *
 * Returns null when the input is not a valid ISO 8601 duration.
 */
export const parseISO8601Duration = (input: string): ISO8601Duration | null => {
    const match = ISO_DURATION_REGEX.exec(input);
    if (!match) {
        return null;
    }

    const totalMonths = toNumber(match[1]) * 12 + toNumber(match[2]);
    const totalDays = toNumber(match[3]) * 7 + toNumber(match[4]);

    return {
        years: Math.floor(totalMonths / 12),
        months: totalMonths % 12,
        weeks: Math.floor(totalDays / 7),
        days: totalDays,
        hours: toNumber(match[5]),
        minutes: toNumber(match[6]),
        seconds: toNumber(match[7]),
    };
};

/**
 * Converts an ISO 8601 duration string to milliseconds, using moment's
 * calendar-component conversions (months become days via the 400-year
 * Gregorian cycle average).
 *
 * Returns 0 for unparseable input, matching moment.duration's behavior of
 * treating invalid strings as a zero duration.
 * TODO: this method is 1:1 paired to replace moment library. It should be properly replaced
 * in the future with a proper date-fns solution/refactor to handle invalid input values
 */
export const isoDurationToMs = (input: string): number => {
    const duration = parseISO8601Duration(input);
    if (!duration) {
        return 0;
    }

    return (
        daysFromMonths(duration.years * 12 + duration.months) * MS_PER_DAY +
        duration.days * MS_PER_DAY +
        duration.hours * MS_PER_HOUR +
        duration.minutes * MS_PER_MINUTE +
        duration.seconds * MS_PER_SECOND
    );
};

/**
 * Formats a number of milliseconds as an ISO 8601 duration string,
 * matching moment.duration(...).toISOString() output
 * (e.g. 'PT7H30M', 'PT26H', 'P0D').
 *
 * Hours are not capped at 24 and sub-minute parts render as seconds,
 * exactly like moment.
 */
export const msToISO8601Duration = (ms: number): string => {
    if (!Number.isFinite(ms)) {
        return 'P0D';
    }

    const sign = ms < 0 ? '-' : '';
    const absMs = Math.abs(ms);

    const hours = Math.floor(absMs / MS_PER_HOUR);
    const minutes = Math.floor((absMs % MS_PER_HOUR) / MS_PER_MINUTE);
    const seconds = (absMs % MS_PER_MINUTE) / MS_PER_SECOND;

    let time = '';
    if (hours > 0) {
        time += `${hours}H`;
    }
    if (minutes > 0) {
        time += `${minutes}M`;
    }
    if (seconds > 0) {
        time += `${seconds}S`;
    }

    // moment renders an empty duration as 'P0D'
    return time ? `${sign}PT${time}` : 'P0D';
};
